import { useEffect, useRef } from "react";
import { toEnglishDigits } from "./loginUtils";

const OTP_LENGTH = 6;

// Six separate inputs for the verification code.
// - Auto-advances on entry, moves back on Backspace when empty
// - Full-code paste fills every box at once
// - Accepts Persian/Arabic digits and normalizes them
// - The row itself is LTR so the digit order is never ambiguous,
//   while labels/hints around it stay RTL with the page.
function OtpInputs({
  value = "",
  onChange,
  onComplete,
  disabled = false,
  hasError = false,
  autoFocus = true,
}) {
  const inputsRef = useRef([]);

  const digits = Array.from({ length: OTP_LENGTH }, (_, i) => value[i] || "");

  useEffect(() => {
    if (autoFocus && !disabled && inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }
  }, [autoFocus, disabled]);

  // When the parent clears the code (e.g. after "resend code"), move focus
  // back to the first box so the user can type immediately.
  const prevValueRef = useRef(value);
  useEffect(() => {
    if (prevValueRef.current && !value && !disabled && inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }
    prevValueRef.current = value;
  }, [value, disabled]);

  const emit = (next) => {
    const joined = next.join("");
    onChange(joined);
    if (joined.length === OTP_LENGTH && /^\d{6}$/.test(joined)) {
      onComplete?.(joined);
    }
  };

  const handleChange = (index, raw) => {
    const normalized = toEnglishDigits(raw).replace(/\D/g, "");
    if (!normalized) return;
    const next = [...digits];
    // Handle multi-char entry (autocomplete / IME) by spreading forward.
    const chars = normalized.slice(0, OTP_LENGTH - index).split("");
    chars.forEach((ch, offset) => {
      next[index + offset] = ch;
    });
    emit(next);
    const focusIndex = Math.min(index + chars.length, OTP_LENGTH - 1);
    inputsRef.current[focusIndex]?.focus();
    if (index + chars.length >= OTP_LENGTH) {
      inputsRef.current[OTP_LENGTH - 1]?.blur();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const next = [...digits];
      if (next[index]) {
        next[index] = "";
        emit(next);
      } else if (index > 0) {
        next[index - 1] = "";
        emit(next);
        inputsRef.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft") {
      // In the LTR row, ArrowLeft moves to the next visual box.
      e.preventDefault();
      inputsRef.current[Math.min(index + 1, OTP_LENGTH - 1)]?.focus();
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      inputsRef.current[Math.max(index - 1, 0)]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = toEnglishDigits(e.clipboardData.getData("text")).replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array.from({ length: OTP_LENGTH }, (_, i) => pasted[i] || "");
    emit(next);
    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    inputsRef.current[focusIndex]?.focus();
    if (pasted.length >= OTP_LENGTH) {
      inputsRef.current[OTP_LENGTH - 1]?.blur();
    }
  };

  return (
    <div
      className={`otp-row${hasError ? " otp-row-error" : ""}`}
      dir="ltr"
      role="group"
      aria-label="کد ورود شش رقمی"
      onPaste={handlePaste}
    >
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            inputsRef.current[index] = el;
          }}
          className={`otp-box${digit ? " otp-box-filled" : ""}`}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          aria-label={`رقم ${index + 1} کد ورود`}
          value={digit}
          maxLength={1}
          disabled={disabled}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </div>
  );
}

export { OTP_LENGTH };
export default OtpInputs;
